/**
 * MonsterDataCache - Performance optimization for monster data normalization
 * 
 * This utility provides caching for normalized monster data to avoid redundant
 * processing of the same monster data across components and re-renders.
 */

class MonsterDataCache {
  constructor() {
    // Initialize cache storage
    this.cache = new Map();
    // Track cache hits and misses for performance monitoring
    this.stats = {
      hits: 0,
      misses: 0,
      size: 0
    };
    // Maximum cache size (adjust based on memory considerations)
    this.maxSize = 100;
    // Cache version for invalidation on schema changes
    this.version = 1;
    // Debug mode for cache diagnostics
    this.debug = false;
  }

  /**
   * Get a normalized monster from cache or return null if not found
   * @param {string} key - Cache key (typically monster ID)
   * @param {number} [dataVersion=null] - Optional data version for versioned caching
   * @returns {Object|null} Cached monster data or null if not in cache
   */
  get(key, dataVersion = null) {
    const cacheKey = this._buildKey(key, dataVersion);
    if (this.cache.has(cacheKey)) {
      const entry = this.cache.get(cacheKey);
      
      // Update last accessed timestamp for LRU eviction policy
      entry.lastAccessed = Date.now();
      this.stats.hits++;
      
      if (this.debug) {
        console.debug(`[MonsterDataCache] CACHE HIT: ${cacheKey}`);
      }
      
      // Return a deep clone to prevent mutation of cached data
      return JSON.parse(JSON.stringify(entry.data));
    }
    
    this.stats.misses++;
    if (this.debug) {
      console.debug(`[MonsterDataCache] CACHE MISS: ${cacheKey}`);
    }
    return null;
  }

  /**
   * Store normalized monster data in cache
   * @param {string} key - Cache key (typically monster ID)
   * @param {Object} data - Normalized monster data to cache
   * @param {number} [dataVersion=null] - Optional data version for versioned caching
   */
  set(key, data, dataVersion = null) {
    if (!key || !data) return;
    
    const cacheKey = this._buildKey(key, dataVersion);
    
    // Evict entries if cache is full
    if (this.cache.size >= this.maxSize && !this.cache.has(cacheKey)) {
      this._evictLRU();
    }
    
    // Store a deep clone to prevent cache pollution
    this.cache.set(cacheKey, {
      data: JSON.parse(JSON.stringify(data)),
      lastAccessed: Date.now(),
      created: Date.now()
    });
    
    this.stats.size = this.cache.size;
    
    if (this.debug) {
      console.debug(`[MonsterDataCache] CACHE SET: ${cacheKey}`);
    }
  }

  /**
   * Check if a key exists in the cache
   * @param {string} key - Cache key to check
   * @param {number} [dataVersion=null] - Optional data version
   * @returns {boolean} True if the key exists in cache
   */
  has(key, dataVersion = null) {
    const cacheKey = this._buildKey(key, dataVersion);
    return this.cache.has(cacheKey);
  }

  /**
   * Remove a specific entry from cache
   * @param {string} key - Cache key to remove
   * @param {number} [dataVersion=null] - Optional data version
   */
  invalidate(key, dataVersion = null) {
    const cacheKey = this._buildKey(key, dataVersion);
    this.cache.delete(cacheKey);
    this.stats.size = this.cache.size;
    
    if (this.debug) {
      console.debug(`[MonsterDataCache] CACHE INVALIDATE: ${cacheKey}`);
    }
  }

  /**
   * Clear the entire cache
   */
  clear() {
    this.cache.clear();
    this.stats.size = 0;
    
    if (this.debug) {
      console.debug('[MonsterDataCache] CACHE CLEARED');
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      ...this.stats,
      hitRatio: this.stats.hits / (this.stats.hits + this.stats.misses) || 0
    };
  }

  /**
   * Enable or disable debug mode
   * @param {boolean} enabled - Whether debug mode should be enabled
   */
  setDebugMode(enabled) {
    this.debug = enabled;
  }

  /**
   * Build a cache key from ID and optional version
   * @private
   */
  _buildKey(key, dataVersion) {
    return dataVersion !== null 
      ? `${key}_v${dataVersion}`
      : key;
  }

  /**
   * Evict least recently used cache entry
   * @private
   */
  _evictLRU() {
    let oldest = null;
    let oldestKey = null;
    
    // Find oldest entry
    for (const [key, entry] of this.cache.entries()) {
      if (oldest === null || entry.lastAccessed < oldest) {
        oldest = entry.lastAccessed;
        oldestKey = key;
      }
    }
    
    // Remove oldest entry
    if (oldestKey) {
      this.cache.delete(oldestKey);
      if (this.debug) {
        console.debug(`[MonsterDataCache] CACHE EVICT LRU: ${oldestKey}`);
      }
    }
  }
}

// Create and export singleton instance
const monsterDataCache = new MonsterDataCache();
export default monsterDataCache;
